import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { ALL_TEMPLATE_READINESS } from '@lims/shared-types';
import { catalogueApi } from '../../api/endpoints.js';
import type { CatalogueItem } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  DataTable,
  FilterBar,
  PageHeader,
  SearchField,
  SelectField,
  StatusBadge,
  type DataColumn,
} from '../../components/ui/index.js';
import { formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { displayName } from '../clients/bilingual.js';
import { useSampleTypes } from '../samples/sample-type-fields.js';
import { RegisterPanel } from '../reports/register-panel.js';

/**
 * The test and service catalogue.
 *
 * TWO HONESTY RULES ARE VISIBLE ON THIS SCREEN.
 *
 * An UNPRICED service reads "not priced", never `0.00`. A price of zero would
 * assert that the laboratory performs the test for nothing, which is a claim
 * nobody made (CLAUDE.md section 8).
 *
 * A test with no specialised template says so, in its own column, on every
 * row. It is not hidden behind a detail page, because a person scanning the
 * list should be able to see at a glance which of these the system can
 * calculate and which it cannot — which today is all of them
 * (CLAUDE.md section 4).
 */
export function CataloguePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const locale = currentLocale();

  const [search, setSearch] = useState('');
  const [sampleType, setSampleType] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // The laboratory's own types (D306). The filter offers archived ones too,
  // marked, since entries classified by them are still listed.
  const sampleTypes = useSampleTypes();

  const catalogue = useQuery({
    queryKey: ['catalogue', { page, search, sampleType, readiness }],
    queryFn: () =>
      catalogueApi.list({
        page,
        search,
        ...(sampleType ? { sampleType } : {}),
        ...(readiness ? { templateReadiness: readiness } : {}),
      }),
  });

  const columns: readonly DataColumn<CatalogueItem>[] = [
    {
      key: 'name',
      header: t('catalogue.name'),
      render: (item) => (
        <div className="flex min-w-0 flex-col items-start">
          {/* The row opens its record (D278). */}
          <Link
            to={`/catalogue/${item.id}`}
            className="font-semibold text-primary-700 underline-offset-2 hover:underline"
          >
            {displayName(item.name, locale) ?? t('common.notRecorded')}
          </Link>
          {item.code ? (
            <span className="measure text-caption text-default-600" dir="ltr">
              {item.code}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'standard',
      header: t('catalogue.referenceStandard'),
      render: (item) =>
        item.referenceStandard ? (
          <span className="text-default-600" dir="ltr">
            {item.standardVersion
              ? `${item.referenceStandard} - ${item.standardVersion}`
              : item.referenceStandard}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'sampleType',
      header: t('catalogue.sampleType'),
      render: (item) =>
        item.sampleType ? (
          <span className="text-default-600">
            {sampleTypes.nameOf(item.sampleType)}
          </span>
        ) : (
          <span className="text-default-600">{t('common.notRecorded')}</span>
        ),
    },
    {
      key: 'price',
      header: t('catalogue.basePrice'),
      align: 'end',
      render: (item) =>
        // NOT `formatMoney(item.basePriceMinor ?? 0)`. An unpriced service is
        // one nobody has costed; showing 0.00 would invent a price.
        item.basePriceMinor === null ? (
          <span className="text-default-600">{t('catalogue.notPriced')}</span>
        ) : (
          <span className="measure text-default-900" dir="ltr">
            {formatMoney(item.basePriceMinor, {
              locale,
              currency: item.currency,
            })}
          </span>
        ),
    },
    {
      key: 'readiness',
      header: t('catalogue.readiness'),
      render: (item) => (
        <StatusBadge
          label={t(`status.templateReadiness.${item.templateReadiness}`)}
          tone={item.templateReadiness === 'ready' ? 'success' : 'warning'}
        />
      ),
    },
    {
      key: 'state',
      header: t('common.status'),
      render: (item) => (
        <StatusBadge
          label={
            item.isActive ? t('common.active') : t('catalogue.deactivated')
          }
          tone={item.isActive ? 'success' : 'neutral'}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      isHeaderHidden: true,
      align: 'end',
      render: (item) => (
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => navigate(`/catalogue/${item.id}`)}
        >
          {t('common.open')}
        </ActionButton>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('catalogue.title')}
        subtitle={t('catalogue.subtitle')}
        breadcrumbs={[{ label: t('nav.catalogue') }]}
        actions={
          can(PERMISSIONS.CATALOGUE_MANAGE) ? (
            <ActionButton
              priority="primary"
              onPress={() => navigate('/catalogue/new')}
            >
              {t('catalogue.create')}
            </ActionButton>
          ) : null
        }
      />

      <RegisterPanel
        toolbar={
          <FilterBar>
            <SearchField
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t('catalogue.searchPlaceholder')}
            />
            <div className="w-full sm:w-56">
              <SelectField
                label={t('catalogue.sampleTypeLabel')}
                value={sampleType}
                onChange={(value) => {
                  setSampleType(value);
                  setPage(1);
                }}
                placeholder={t('common.all')}
                options={sampleTypes.types.map((type) => ({
                  value: type.code,
                  label: type.archived
                    ? t('catalogue.sampleTypeArchived', { name: sampleTypes.nameOf(type.code) })
                    : sampleTypes.nameOf(type.code),
                }))}
              />
            </div>
            <div className="w-full sm:w-56">
              <SelectField
                label={t('catalogue.readinessLabel')}
                value={readiness}
                onChange={(value) => {
                  setReadiness(value);
                  setPage(1);
                }}
                placeholder={t('common.all')}
                options={ALL_TEMPLATE_READINESS.map((value) => ({
                  value,
                  label: t(`status.templateReadiness.${value}`),
                }))}
              />
            </div>
          </FilterBar>
        }
      >
        <DataTable
          label={t('catalogue.title')}
          columns={columns}
          rows={catalogue.data?.data ?? []}
          rowKey={(item) => item.id}
          isLoading={catalogue.isLoading}
          error={catalogue.error}
          onRetry={() => void catalogue.refetch()}
          emptyContent={
            search ? t('catalogue.noneFound') : t('catalogue.noneYet')
          }
          page={page}
          totalPages={catalogue.data?.meta.totalPages ?? 1}
          onPageChange={setPage}
        />
      </RegisterPanel>
    </>
  );
}
